import type { FontInfo } from '@remotion/google-fonts'
import { loadFontFromInfo } from '@remotion/google-fonts/from-info'
import { useLayoutEffect, useMemo, useState } from 'react'
import { useDelayRender } from 'remotion'
import { FONTS } from '../utils/fonts'
import type { FontStyle } from './types'

/**
 * Font loading for text and caption layers (#77).
 *
 * The Starter fetched font metadata from an API route backed by a 4 MB
 * Google Fonts database. Here the catalogue is `FONTS`, the same list the
 * templates render with, and an item's `fontFamily` is the CSS family name.
 * The loader is a dynamic import, so it works the same on Lambda and in the
 * browser: no "font infos during rendering" hand-off is needed.
 */

export const DEFAULT_FONT_FAMILY = FONTS.some((f) => f.family === 'Roboto')
  ? 'Roboto'
  : FONTS[0]!.family

const fontInfoPromiseCache: Record<string, Promise<FontInfo>> = {}

function resolveFontEntry(fontFamily: string) {
  const entry = FONTS.find((f) => f.family === fontFamily)
  if (entry) return entry
  console.warn(
    `Font "${fontFamily}" is not in FONTS, falling back to "${DEFAULT_FONT_FAMILY}"`,
  )
  return FONTS.find((f) => f.family === DEFAULT_FONT_FAMILY) ?? FONTS[0]!
}

export function loadFontInfo(fontFamily: string): Promise<FontInfo> {
  if (!fontInfoPromiseCache[fontFamily]) {
    fontInfoPromiseCache[fontFamily] = (async () => {
      const entry = resolveFontEntry(fontFamily)
      const mod = (await entry.load()) as { getInfo: () => FontInfo }
      return mod.getInfo()
    })()
  }
  return fontInfoPromiseCache[fontFamily]!
}

export function getFontVariants(fontInfo: FontInfo): FontStyle[][] {
  const styles: FontStyle[][] = []
  const variants = Object.keys(fontInfo.fonts)
  const sortedVariants = [
    ...variants.filter((v) => v.toLowerCase() === 'normal'),
    ...variants.filter((v) => v.toLowerCase() !== 'normal').sort(),
  ]
  for (const variant of sortedVariants) {
    const row: FontStyle[] = []
    for (const weight of Object.keys(
      (fontInfo.fonts as Record<string, Record<string, unknown>>)[variant] ??
        {},
    )) {
      row.push({ variant, weight })
    }
    styles.push(row)
  }
  return styles
}

/** The closest available `{ variant, weight }` to what the item asks for. */
export function nearestFontStyle(
  fontInfo: FontInfo,
  wanted: FontStyle,
): FontStyle {
  const fonts = fontInfo.fonts as Record<string, Record<string, unknown>>
  const variant = fonts[wanted.variant]
    ? wanted.variant
    : fonts['normal']
      ? 'normal'
      : Object.keys(fonts)[0]!
  const weights = Object.keys(fonts[variant] ?? {})
  if (weights.includes(wanted.weight)) return { variant, weight: wanted.weight }
  const target = Number(wanted.weight)
  const nearest = weights.reduce((best, w) =>
    Math.abs(Number(w) - target) < Math.abs(Number(best) - target) ? w : best,
  )
  return { variant, weight: nearest }
}

export async function loadFont({
  fontFamily,
  fontVariant,
  fontWeight,
}: {
  fontFamily: string
  fontVariant: string
  fontWeight: string
}): Promise<void> {
  const fontInfo = await loadFontInfo(fontFamily)
  const style = nearestFontStyle(fontInfo, {
    variant: fontVariant,
    weight: fontWeight,
  })
  await loadFontFromInfo(fontInfo, style.variant, {
    weights: [style.weight],
  }).waitUntilDone()
}

/** Loads the font and holds the render until it is ready. */
export function useLoadFont({
  fontFamily,
  fontVariant,
  fontWeight,
}: {
  fontFamily: string
  fontVariant: string
  fontWeight: string
}): boolean {
  const [loaded, setLoaded] = useState(false)
  const { cancelRender, continueRender, delayRender } = useDelayRender()
  const promise = useMemo(
    () => loadFont({ fontFamily, fontVariant, fontWeight }),
    [fontFamily, fontVariant, fontWeight],
  )

  useLayoutEffect(() => {
    const handle = delayRender('Loading font')
    promise
      .then(() => {
        continueRender(handle)
        setLoaded(true)
      })
      .catch((err) => cancelRender(err))
  }, [cancelRender, continueRender, delayRender, promise])

  return loaded
}

export function turnFontStyleIntoCss(
  fontStyle: FontStyle,
): React.CSSProperties {
  return {
    ...(fontStyle.variant.toLowerCase().includes('italic')
      ? { fontStyle: 'italic' as const }
      : {}),
    fontWeight: fontStyle.weight,
  }
}
